/**
 * Search Result Type Registry
 *
 * Plugin-extensible registry for search result renderers.
 * Plugins register their entity types with icons, preview renderers,
 * card renderers, and optional filter components.
 *
 * The global search UI discovers all registered types dynamically —
 * no entity types are hard-coded anywhere.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ComponentType, ReactNode } from 'react';

// ── Types ───────────────────────────────────────────────────────────

export interface SearchResultTypeRegistration {
  /** Unique entity type key (e.g., 'note', 'meeting', 'todo') */
  type: string;
  /** Small icon component for overlay results */
  icon: ComponentType<{ size?: number; className?: string }>;
  /** Preview snippet renderer for overlay dropdown */
  renderPreview: (entity: any) => ReactNode;
  /** Full card renderer for search results page */
  renderCard: (entity: any) => ReactNode;
  /** Optional plugin-specific filter component for search page */
  renderFilters?: () => ReactNode;
  /** Human-readable label for this result type (e.g. "Notes") */
  label?: string;
  /** Plugin that registered this type */
  pluginId?: string;
  /** Sort priority (lower = first) */
  order?: number;
}

// ── Store ───────────────────────────────────────────────────────────

interface SearchResultTypeRegistryState {
  /** All registered search result types, keyed by type string */
  types: Map<string, SearchResultTypeRegistration>;
  /** Track which plugins registered which types */
  pluginTypes: Map<string, Set<string>>;

  /** Register a single search result type */
  registerSearchResultType: (registration: SearchResultTypeRegistration) => void;
  /** Unregister all search result types from a specific plugin */
  unregisterPlugin: (pluginId: string) => void;

  /** Get registration for a specific type */
  getType: (type: string) => SearchResultTypeRegistration | undefined;
  /** Get all registered types */
  getAllTypes: () => SearchResultTypeRegistration[];
  /** Get all type keys */
  getTypeKeys: () => string[];
  /** Check if a type is registered */
  hasType: (type: string) => boolean;
}

export const useSearchResultTypeRegistry = create<SearchResultTypeRegistryState>()(
  devtools(
    (set, get) => ({
      types: new Map(),
      pluginTypes: new Map(),

      registerSearchResultType: (registration: SearchResultTypeRegistration) => {
        set((state) => {
          const newTypes = new Map(state.types);
          newTypes.set(registration.type, registration);

          const newPluginTypes = new Map(state.pluginTypes);
          const pluginId = registration.pluginId ?? '__core__';
          const existing = newPluginTypes.get(pluginId) ?? new Set();
          existing.add(registration.type);
          newPluginTypes.set(pluginId, existing);

          return { types: newTypes, pluginTypes: newPluginTypes };
        });
      },

      unregisterPlugin: (pluginId: string) => {
        set((state) => {
          const typeKeys = state.pluginTypes.get(pluginId);
          if (!typeKeys) return state;

          const newTypes = new Map(state.types);
          for (const key of typeKeys) {
            newTypes.delete(key);
          }

          const newPluginTypes = new Map(state.pluginTypes);
          newPluginTypes.delete(pluginId);

          return { types: newTypes, pluginTypes: newPluginTypes };
        });
      },

      getType: (type: string) => get().types.get(type),

      getAllTypes: () => {
        const arr = Array.from(get().types.values());
        arr.sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
        return arr;
      },

      getTypeKeys: () => Array.from(get().types.keys()),

      hasType: (type: string) => get().types.has(type),
    }),
    { name: 'search-result-type-registry' },
  ),
);
